import { ActionIcon, Alert, Badge, Button, Group, Loader, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDeleteFundCall } from 'src/Hooks/Mutations/useFundCallMutations';
import { useFundCalls } from 'src/Hooks/Queries/useFundCalls';
import { FundCallDto, FundCallDtoStatusEnum } from 'src/Services/API/generated';
import { trackFundCallDraftDeleted } from 'src/Services/Mixpanel/events';

import styles from './FundCallList.module.scss';
import { Props } from './FundCallList.types';

/** Onglet « Appels de fonds » : ce qui a été émis, et où en sont les versements. */
const FundCallList = ({ canIssueForAll }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: fundCalls, isLoading, isError, refetch } = useFundCalls();
  const deleteFundCall = useDeleteFundCall();

  // Brouillon seulement : un appel envoyé est un historique, l'API refuse de le supprimer.
  const confirmDelete = (fundCall: FundCallDto) => {
    modals.openConfirmModal({
      title: t('fundCalls.list.deleteTitle', 'Supprimer ce brouillon ?'),
      children: (
        <Text size="sm">
          {t(
            'fundCalls.list.deleteBody',
            '« {{title}} » n’a pas été envoyé. Il disparaîtra de la liste, personne n’est prévenu.',
            { title: fundCall.title }
          )}
        </Text>
      ),
      labels: {
        confirm: t('common.delete', 'Supprimer'),
        cancel: t('common.cancel', 'Annuler'),
      },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        deleteFundCall.mutate(fundCall.id, {
          onSuccess: () => trackFundCallDraftDeleted({ type: fundCall.type }),
          onError: () => {
            notifications.show({
              color: 'red',
              message: t('fundCalls.list.deleteFailed', 'Le brouillon n’a pas pu être supprimé.'),
            });
          },
        }),
    });
  };

  if (isLoading) return <Loader />;

  // Un échec ne doit jamais se déguiser en « rien à afficher » : le gestionnaire conclurait
  // qu'il n'a pas d'appel de fonds alors que la requête n'a pas abouti.
  if (isError) {
    return (
      <Alert color="red" title={t('fundCalls.list.errorTitle', 'Chargement impossible')}>
        {t(
          'fundCalls.list.errorBody',
          'Les appels de fonds n’ont pas pu être chargés. Réessayez dans un instant.'
        )}
        <Button variant="outline" size="xs" mt="sm" onClick={() => refetch()}>
          {t('common.retry', 'Réessayer')}
        </Button>
      </Alert>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('fundCalls.list.title', 'Appels de fonds et relevés')}</h2>
        {canIssueForAll && (
          <Button leftSection={<IconPlus size={18} />} onClick={() => navigate('fund-calls/new')}>
            {t('fundCalls.list.new', 'Nouvel appel de fonds')}
          </Button>
        )}
      </div>

      {(fundCalls ?? []).length === 0 ? (
        <p className={styles.empty}>
          {t(
            'fundCalls.list.empty',
            'Aucun appel de fonds pour le moment. Le premier fait sortir le budget de votre écran.'
          )}
        </p>
      ) : (
        (fundCalls ?? []).map(fundCall => (
          <div
            key={fundCall.id}
            className={styles.row}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`fund-calls/${fundCall.id}`)}
            onKeyDown={event => {
              if (event.key === 'Enter') navigate(`fund-calls/${fundCall.id}`);
            }}
          >
            <div>
              <div className={styles.title}>{fundCall.title}</div>
              <div className={styles.meta}>
                {fundCall.periodLabel}
                {fundCall.dueDate
                  ? ` · ${t('fundCalls.list.dueOn', 'échéance {{date}}', {
                      date: dayjs(fundCall.dueDate).format('DD/MM/YYYY'),
                    })}`
                  : ''}
              </div>
            </div>

            {fundCall.status === FundCallDtoStatusEnum.Draft ? (
              <Group gap="xs" wrap="nowrap">
                <Badge variant="light" color="gray">
                  {t('fundCalls.status.draft', 'Brouillon')}
                </Badge>
                {canIssueForAll && (
                  <>
                    <Tooltip label={t('fundCalls.list.edit', 'Modifier')}>
                      <ActionIcon
                        variant="subtle"
                        aria-label={t('fundCalls.list.edit', 'Modifier')}
                        onClick={event => {
                          // La ligne entière ouvre le détail : le stylo ne doit pas le déclencher.
                          event.stopPropagation();
                          navigate(`fund-calls/${fundCall.id}/edit`);
                        }}
                      >
                        <IconPencil size={18} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t('fundCalls.list.delete', 'Supprimer')}>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={t('fundCalls.list.delete', 'Supprimer')}
                        onClick={event => {
                          event.stopPropagation();
                          confirmDelete(fundCall);
                        }}
                      >
                        <IconTrash size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </>
                )}
              </Group>
            ) : (
              <Badge
                variant="light"
                color={fundCall.settledCount === fundCall.calledCount ? 'teal' : 'orange'}
              >
                {t('fundCalls.list.settledCount', '{{settled}}/{{called}} versés', {
                  settled: fundCall.settledCount,
                  called: fundCall.calledCount,
                })}
              </Badge>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default FundCallList;
